import React, { useEffect, useState } from "react";
import axios from "axios";
import { convertToArabicNumbers } from "@/helpers";
import {
  BookOpen,
  Search,
  Bookmark,
  Library,
  Box,
  History,
  Trash2,
  HelpCircle,
  Sparkles,
  HandHeart,
  Compass,
  BookMarked,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ThemeToggle from "@/components/ThemeToggle";
import DailyWidget from "@/components/DailyWidget";
import PrayerTimes from "@/components/PrayerTimes";
import ShalatTimes from "@/components/ShalatTimes";
import MobileHeader from "@/components/MobileHeader";
import useBookmark from "@/hooks/useBookmark";
import { querySmartSearch } from "@/helpers/smartSearch";

export default function SurahList() {
  const [loading, setLoading] = useState(false);
  const [surahList, setSurahList] = useState([]);
  const [surahAll, setSurahAll] = useState([]);
  const {
    bookmarks: bookmark,
    addBookmark,
    removeBookmark,
    isBookmark,
  } = useBookmark();
  const [viewBookmark, setViewBookmark] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState([]);

  const getSurahList = async () => {
    setLoading(true);
    await axios
      .get(`https://equran.id/api/v2/surat`)
      .then(async (res) => {
        setSurahList(res.data.data);
        setSurahAll(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
    setLoading(false);
  };

  const searchSurah = (event) => {
    let query = event.target.value.toLowerCase().trim();
    let baseList = viewBookmark ? bookmark : surahAll;
    
    if (query) {
      const suggestions = querySmartSearch(query);
      setSmartSuggestions(suggestions);
    } else {
      setSmartSuggestions([]);
    }

    let updatedList = baseList.filter(function (item) {
      return (
        item.namaLatin.toLowerCase().search(query) !== -1 ||
        item.arti.toLowerCase().search(query) !== -1 ||
        item.nomor.toString() === query
      );
    });
    setSurahList(updatedList);
  };

  const getLastRead = () => {
    const data = localStorage.getItem("lastRead");
    if (data) {
      setLastRead(JSON.parse(data));
    }
  };

  const deleteLastRead = () => {
    localStorage.removeItem("lastRead");
    setLastRead(null);
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    getSurahList();
    getLastRead();
  }, []);

  // Check if current Hijri month is Ramadhan (9th month)
  const isRamadhan = () => {
    try {
      const today = new Date();
      // Koreksi -1 hari untuk standar Indonesia
      const adjustedDate = new Date(today);
      adjustedDate.setDate(today.getDate() - 1);

      const hijriMonth = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
        month: "numeric",
      }).format(adjustedDate);
      return hijriMonth === "9";
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      <SEO
        title="Baca Online 30 Juz"
        description="Baca Al-Qur'an secara online di Al-Qur'an Indonesia. Tersedia 114 Surah lengkap dengan terjemahan Bahasa Indonesia dan audio."
        isHomePage={true}
      />
      {/* ===== MOBILE HEADER (hanya mobile) ===== */}
      <MobileHeader title="Al-Qur'an Indonesia" />

      {/* ===== DESKTOP HERO (disembunyikan di mobile) ===== */}
      <div className="hidden md:block relative overflow-hidden bg-[#0a2e26] dark:bg-slate-950 pt-20 pb-16 sm:py-24 px-4 sm:px-6">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/islamic-art.png')]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/50 to-[#0a2e26] dark:to-slate-950"></div>
        </div>

        <div className="absolute top-6 right-6 z-50 flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/bantuan"
            className="p-2.5 bg-white/10 hover:bg-white/20 rounded-2xl backdrop-blur-md text-white transition-all active:scale-95 group border border-white/10"
            title="Pusat Bantuan"
          >
            <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          </Link>
        </div>

        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 rounded-full border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-[0.2em] animate-fade-in">
            Al-Qur'an Indonesia Digital
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white tracking-tight animate-slide-up leading-[1.1]">
            Baca <span className="text-emerald-400">Al-Qur'an</span> di mana
            saja.
          </h1>

          <p className="text-lg text-emerald-100/60 max-w-xl mx-auto leading-relaxed animate-fade-in delay-100 px-4">
            Al-Qur'an digital Indonesia lengkap dengan terjemahan, tafsir, dan
            audio jernih dalam genggaman Anda.
          </p>

          <div className="flex items-center justify-center pt-4 animate-fade-in delay-200">
            <Link
              to="/tanya-ai"
              className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-white rounded-[2rem] font-bold transition-all shadow-xl shadow-emerald-500/20 active:scale-95 group"
            >
              <Sparkles
                size={20}
                className="group-hover:rotate-12 transition-transform"
              />
              Cari Ayat & Doa
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 md:py-12 content-with-bottom-nav md:pb-0">
        {!viewBookmark && (
          <div className="space-y-8 mb-12">
            <DailyWidget />
            {isRamadhan() ? <PrayerTimes /> : <ShalatTimes />}
          </div>
        )}

        {/* Search & Action Section */}
        <div className="mb-12 space-y-6">
          <div className="w-full animate-fade-in">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <Search
                  className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors"
                  strokeWidth={2.5}
                />
              </div>
              <input
                onChange={searchSurah}
                type="search"
                className="w-full pl-12 pr-5 py-3.5 md:py-5 text-base md:text-lg border border-gray-100 dark:border-slate-800 rounded-full md:rounded-3xl bg-white dark:bg-slate-900 shadow-xl shadow-emerald-500/5 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 focus:outline-none transition-all duration-300 dark:text-white dark:placeholder-gray-500"
                placeholder="Cari Surah..."
              />
            </div>
          </div>
          
          {/* Smart Search Suggestions */}
          {smartSuggestions.length > 0 && (
            <div className="w-full animate-fade-in mt-6 space-y-3">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider px-2">Hasil Pintar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {smartSuggestions.map((sug) => {
                  const Icon = sug.type === 'juz' ? BookMarked : sug.type === 'ayat' ? BookOpen : Compass;
                  return (
                    <Link
                      key={sug.id}
                      to={sug.path}
                      className="flex items-center gap-4 p-4 rounded-[1.5rem] bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-500/20 hover:shadow-2xl hover:shadow-emerald-500/30 transition-all group active:scale-[0.98]"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform">
                        <Icon size={24} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold truncate">{sug.title}</h3>
                        <p className="text-emerald-50 text-xs truncate">{sug.subtitle}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Buttons — tersembunyi di mobile (sudah ada BottomNavigation) */}
          <div className="hidden sm:grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full animate-fade-in delay-100">
            <button
              onClick={() => {
                setViewBookmark(!viewBookmark);
                viewBookmark ? setSurahList(surahAll) : setSurahList(bookmark);
              }}
              className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-6 py-4 rounded-2xl bg-white dark:bg-slate-900 shadow-sm border border-gray-100 dark:border-slate-800 hover:border-emerald-500/30 hover:shadow-lg transition-all duration-300 dark:text-gray-200 font-bold group"
            >
              {viewBookmark ? (
                <Library className="w-5 h-5 group-hover:scale-110 transition-transform text-emerald-500" />
              ) : (
                <Bookmark className="w-5 h-5 group-hover:scale-110 transition-transform text-emerald-500" />
              )}
              <span className="text-xs sm:text-sm font-medium">
                {viewBookmark ? "Semua Surah" : "Bookmark"}
              </span>
            </button>

            <Link
              to="/juz"
              className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-6 py-4 rounded-2xl bg-white dark:bg-slate-900 shadow-sm border border-gray-100 dark:border-slate-800 hover:border-emerald-500/30 hover:shadow-lg transition-all duration-300 dark:text-gray-200 font-bold group"
            >
              <BookOpen className="w-5 h-5 group-hover:scale-110 transition-transform text-emerald-500" />
              <span className="text-xs sm:text-sm font-medium">
                Baca per Juz
              </span>
            </Link>

            <Link
              to="/doa"
              className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-6 py-4 rounded-2xl bg-white dark:bg-slate-900 shadow-sm border border-gray-100 dark:border-slate-800 hover:border-emerald-500/30 hover:shadow-lg transition-all duration-300 dark:text-gray-200 font-bold group"
            >
              <HandHeart className="w-5 h-5 group-hover:scale-110 transition-transform text-emerald-500" />
              <span className="text-xs sm:text-sm font-medium">Doa Harian</span>
            </Link>

            <Link
              to="/bantuan"
              className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-6 py-4 rounded-2xl bg-white dark:bg-slate-900 shadow-sm border border-gray-100 dark:border-slate-800 hover:border-emerald-500/30 hover:shadow-lg transition-all duration-300 dark:text-gray-200 font-bold group"
            >
              <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform text-emerald-500" />
              <span className="text-xs sm:text-sm font-medium">Bantuan</span>
            </Link>
          </div>

          {/* Mobile — Bookmark toggle */}
          <div className="flex sm:hidden items-center gap-2">
            <button
              onClick={() => {
                setViewBookmark(!viewBookmark);
                viewBookmark ? setSurahList(surahAll) : setSurahList(bookmark);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border transition-all active:scale-95 font-bold text-sm ${
                viewBookmark
                  ? "bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20"
                  : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200"
              }`}
            >
              {viewBookmark ? (
                <Library className="w-4 h-4" />
              ) : (
                <Bookmark className="w-4 h-4" />
              )}
              {viewBookmark ? "Semua Surah" : "Bookmark"}
            </button>
          </div>
        </div>

        {/* Last Read Card */}
        {!viewBookmark && lastRead && (
          <div className="mb-8 animate-fade-in relative z-10">
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-800 rounded-[2rem] p-6 shadow-[0_20px_40px_rgb(16,185,129,0.25)] dark:shadow-[0_20px_40px_rgb(16,185,129,0.15)] text-white border border-white/10 backdrop-blur-3xl">
              <div className="absolute top-1/2 -right-4 -translate-y-1/2 opacity-[0.03] pointer-events-none">
                <History size={120} strokeWidth={1} />
              </div>

              <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="flex-shrink-0 p-3.5 bg-white/10 rounded-2xl backdrop-blur-md border border-white/20 shadow-lg">
                    <History className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-400/20 text-emerald-300 px-2 py-0.5 rounded-md">
                        Terakhir Dibaca
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white leading-tight">
                      {lastRead.type === "juz"
                        ? `Juz ${lastRead.juz}`
                        : lastRead.namaLatin}
                    </h3>
                    <p className="text-white/70 text-sm">
                      {lastRead.type === "juz"
                        ? `${lastRead.namaLatin} Ayat ${lastRead.nomorAyat}`
                        : `Ayat ${lastRead.nomorAyat}`}{" "}
                      <span className="mx-1 • text-white/30">•</span> Lanjutkan
                      Membaca
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <Link
                    to={
                      lastRead.type === "juz"
                        ? `/juz/${lastRead.juz}#verse-${lastRead.nomorSurah}-${lastRead.nomorAyat}`
                        : `/baca/${lastRead.nomorSurah}#verse-${lastRead.nomorAyat}`
                    }
                    className="flex-1 md:flex-none inline-flex items-center justify-center px-6 py-2.5 bg-white text-emerald-800 rounded-xl font-bold text-sm hover:bg-emerald-50 transition-all duration-200 shadow-md active:scale-95"
                  >
                    <History className="w-4 h-4 mr-2" strokeWidth={2.5} />
                    Lanjutkan Membaca
                  </Link>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex-none p-2.5 bg-white/10 hover:bg-red-500/20 border border-white/20 rounded-xl transition-all duration-200 active:scale-95 text-white"
                    title="Hapus Penanda"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Surah List — Grid di desktop, list compact di mobile */}
        <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.1)] md:bg-transparent md:shadow-none md:dark:bg-transparent grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 md:gap-6 overflow-hidden md:overflow-visible border border-gray-100 dark:border-slate-700 md:border-none">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className={`p-4 md:p-6 bg-white dark:bg-slate-800 md:rounded-2xl md:shadow-sm animate-pulse md:border border-gray-100 dark:border-slate-700 flex md:flex-col items-center md:items-start gap-4 md:gap-0 ${index !== 5 ? 'border-b border-gray-50 dark:border-slate-700/50 md:border-b-gray-100' : ''}`}
              >
                <div className="flex md:items-start md:justify-between w-full md:mb-4 gap-4 md:gap-0">
                  <div className="w-10 h-10 md:w-16 md:h-16 bg-gray-200 dark:bg-slate-700 rounded-xl md:rounded-full"></div>
                  <div className="hidden md:block w-6 h-6 bg-gray-200 dark:bg-slate-700 rounded"></div>
                  <div className="md:hidden flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/3"></div>
                  </div>
                </div>
                <div className="hidden md:block space-y-3 w-full">
                  <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3"></div>
                </div>
              </div>
            ))
          ) : surahList.length === 0 ? (
            <div className="col-span-full text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-slate-800/50 rounded-full flex items-center justify-center">
                <Box className="w-12 h-12 text-gray-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Tidak ada data ditemukan
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Coba ubah kata kunci pencarian Anda
              </p>
            </div>
          ) : (
            surahList.map((item, index) => (
              <div
                key={item.nomor}
                className={`group bg-white dark:bg-slate-800 md:rounded-2xl md:shadow-[0_2px_10px_rgba(0,0,0,0.02)] md:hover:shadow-xl dark:md:hover:shadow-emerald-900/10 transition-all duration-300 overflow-hidden md:border border-gray-100 dark:border-slate-700 md:hover:border-emerald-200 dark:md:hover:border-emerald-800 ${index !== surahList.length - 1 ? 'border-b border-gray-50 dark:border-slate-700/50 md:border-b-gray-100' : ''}`}
              >
                {/* ===== DESKTOP CARD (hidden on mobile) ===== */}
                <div className="hidden md:block p-6">
                  <div className="flex items-start justify-between mb-4">
                    <a
                      href={`/baca/${item.nomor}`}
                      className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 dark:from-emerald-600 dark:to-emerald-800 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-105 transition-transform duration-200"
                    >
                      {convertToArabicNumbers(item.nomor)}
                    </a>

                    {!viewBookmark && (
                      <button
                        onClick={() => {
                          if (isBookmark(item.nomor))
                            removeBookmark(item.nomor);
                          else addBookmark(item);
                        }}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
                      >
                        <Bookmark
                          className={`w-6 h-6 transition-all duration-300 ${
                            isBookmark(item.nomor)
                              ? "fill-emerald-600 text-emerald-600"
                              : "text-gray-400"
                          }`}
                          strokeWidth={2}
                        />
                      </button>
                    )}
                  </div>

                  <a href={`/baca/${item.nomor}`} className="block">
                    <h3
                      className="text-2xl font-arabic font-bold text-gray-900 dark:text-white mb-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200 text-right"
                      dir="rtl"
                    >
                      {item.nama}
                    </h3>
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">
                      {item.namaLatin}
                    </p>
                    <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                      {item.arti}
                    </p>
                  </a>
                </div>

                {/* ===== MOBILE LIST ROW (hidden on desktop) ===== */}
                <a
                  href={`/baca/${item.nomor}`}
                  className="md:hidden flex items-center gap-4 p-4 active:bg-gray-50 dark:active:bg-slate-700/50 transition-colors"
                >
                  {/* Nomor */}
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-md shadow-emerald-500/20">
                    {convertToArabicNumbers(item.nomor)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-bold text-gray-900 dark:text-white text-sm truncate">
                        {item.namaLatin}
                      </span>
                      <span
                        className="font-arabic text-base text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                        dir="rtl"
                      >
                        {item.nama}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {item.jumlahAyat} ayat
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500 capitalize">
                        {item.tempatTurun}
                      </span>
                    </div>
                  </div>

                  {/* Bookmark button */}
                  {!viewBookmark && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isBookmark(item.nomor)) removeBookmark(item.nomor);
                        else addBookmark(item);
                      }}
                      className="flex-shrink-0 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors active:scale-90"
                      aria-label={isBookmark(item.nomor) ? "Hapus bookmark" : "Tambah bookmark"}
                    >
                      <Bookmark
                        className={`w-4 h-4 transition-all ${
                          isBookmark(item.nomor)
                            ? "fill-emerald-500 text-emerald-500"
                            : "text-gray-300 dark:text-gray-600"
                        }`}
                        strokeWidth={2}
                      />
                    </button>
                  )}
                </a>

                {/* Desktop footer */}
                <div className="hidden md:block px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-700">
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>{item.jumlahAyat} Ayat</span>
                    <span className="capitalize">{item.tempatTurun}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-scale-up">
            <div className="p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <Trash2
                  className="w-8 h-8 text-red-600 dark:text-red-400"
                  strokeWidth={2.5}
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Hapus Terakhir Dibaca?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Anda akan menghapus penanda terakhir dibaca di{" "}
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  Surah {lastRead?.namaLatin} Ayat {lastRead?.nomorAyat}
                </span>
                . Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-6 py-3 rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors text-gray-700 dark:text-gray-200 font-semibold"
                >
                  Batal
                </button>
                <button
                  onClick={deleteLastRead}
                  className="flex-1 px-6 py-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors text-white font-semibold shadow-lg shadow-red-600/20 active:scale-95"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
